import { Modal, Button, Loader, Table, Tag } from "rsuite";
import styles from "./ImportPricesPreviewModal.module.css";
import { useImportPricesPreviewModal } from "./useImportPricesPreviewModal";

const { Column, HeaderCell, Cell } = Table;

function ImportPricesPreviewModal() {
  const { display, loading, saving, vendor, result, confirm, closeModal } =
    useImportPricesPreviewModal();

  const matched = result?.matched || [];
  const unmatched = result?.unmatched || [];
  const ccy = vendor?.currency || "USD";

  return (
    <Modal size="lg" open={display} onClose={closeModal} overflow>
      <Modal.Header>
        <Modal.Title>
          Import quotation prices
          {vendor?.vendorName ? ` - ${vendor.vendorName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh" }}>
        {loading ? (
          <div className={styles.loading}>
            <Loader size="md" />
            <p>Reading and matching the quotation…</p>
          </div>
        ) : !result ? (
          <p>No data parsed.</p>
        ) : (
          <>
            <div className={styles.summary}>
              <Tag color="green">{matched.length} matched</Tag>
              {unmatched.length > 0 && (
                <Tag color="orange">{unmatched.length} unmatched</Tag>
              )}
              <span className={styles.summary_note}>
                {result.totalLines} line(s) in file · prices applied in{" "}
                <strong>{ccy}</strong>
              </span>
            </div>

            {matched.length > 0 && (
              <>
                <div className={styles.section_title}>
                  Matched - these prices will be filled
                </div>
                <Table
                  data={matched}
                  autoHeight
                  bordered
                  cellBordered
                  rowHeight={36}
                  className={styles.table}
                >
                  <Column width={70} align="center">
                    <HeaderCell>Qtn No</HeaderCell>
                    <Cell dataKey="quotationNo" />
                  </Column>
                  <Column width={110}>
                    <HeaderCell>Code</HeaderCell>
                    <Cell dataKey="quotationCode" />
                  </Column>
                  <Column flexGrow={2}>
                    <HeaderCell>Our item</HeaderCell>
                    <Cell>
                      {(row) => (
                        <span title={row.itemName}>
                          {row.itemPartNo ? `${row.itemPartNo} · ` : ""}
                          {row.itemName}
                        </span>
                      )}
                    </Cell>
                  </Column>
                  <Column width={117} align="right">
                    <HeaderCell>Total</HeaderCell>
                    <Cell>
                      {(row) => (
                        <strong>
                          {row.price} {ccy}
                        </strong>
                      )}
                    </Cell>
                  </Column>
                  <Column width={90} align="center">
                    <HeaderCell>Matched by</HeaderCell>
                    <Cell>
                      {(row) => (
                        <Tag
                          size="sm"
                          color={row.matchedBy === "code" ? "blue" : "violet"}
                        >
                          {row.matchedBy}
                        </Tag>
                      )}
                    </Cell>
                  </Column>
                </Table>
              </>
            )}

            {unmatched.length > 0 && (
              <>
                <div className={styles.section_title}>
                  Unmatched - no item found, skipped
                </div>
                <Table
                  data={unmatched}
                  autoHeight
                  bordered
                  cellBordered
                  rowHeight={34}
                  className={styles.table_muted}
                >
                  <Column width={70} align="center">
                    <HeaderCell>Qtn No</HeaderCell>
                    <Cell dataKey="quotationNo" />
                  </Column>
                  <Column width={110}>
                    <HeaderCell>Code</HeaderCell>
                    <Cell dataKey="quotationCode" />
                  </Column>
                  <Column flexGrow={2}>
                    <HeaderCell>Description</HeaderCell>
                    <Cell dataKey="quotationDescription" />
                  </Column>
                  <Column width={117} align="right">
                    <HeaderCell>Total</HeaderCell>
                    <Cell>
                      {(row) => (row.total != null ? row.total : "-")}
                    </Cell>
                  </Column>
                </Table>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={closeModal} appearance="subtle" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={confirm}
          appearance="primary"
          color="blue"
          loading={saving}
          disabled={loading || !matched.length}
        >
          {matched.length
            ? `Fill ${matched.length} price${matched.length > 1 ? "s" : ""}`
            : "Nothing to import"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ImportPricesPreviewModal;
